import React, { Component } from 'react';
import { object, bool, func } from 'prop-types';
import { contains } from 'ramda';

// Redux
import { connect } from 'react-redux';
import {
  closeAlertDialog,
  closeConfirmDialog,
  openConfirmDialog,
  openErrorDialog,
  openSuccessDialog,
} from '../../../../actions/dialog/dialogActions';
import {
  deleteMessage,
  getAllMessageThreadHeaders,
  forwardMessage,
  getMessage,
  getPossibleRecipients,
  replyMessage,
  sendMessage,
} from '../../../../actions/messages/messagesActions';
import { setHeaderTitle } from '../../../../actions/header/headerActions';
import { setMetadata } from '../../../../actions/metadata/metadataActions';
import {
  FETCH_MESSAGE_THREAD_HEADERS,
  FORWARD_MESSAGE,
  REPLY_MESSAGE,
  SEND_MESSAGE,
} from '../../../../actions/messages/types';

// Header Components
import RefreshMsgsButton from '../RefreshMsgsButton';
import NewMsgButton from '../NewMsgButton';
import MessageThreadUtilities from '../MessageThreadUtilities';

// Main Layout Components
import Layout from '../../../Layout';
import ActionsPanel from '../../../ActionsPanel';
import MessageThreadHeaderContainer from '../MessageThreadHeaderContainer';
import MessageThreadContainer from '../MessageThreadContainer';
import MessageForm from '../MessageForm';
import ContentPanel from '../../../ContentPanel';

import cx from 'classnames';
import s from './LandingPage.css';


export class LandingPage extends Component {
  static propTypes = {
    authenticatedPreferences: object,
    closeAlertDialog: func,
    closeConfirmDialog: func,
    getAllMessageThreadHeaders: func,
    forwardMessage: func,
    getMessage: func,
    getPossibleRecipients: func,
    isCollapsed: bool,
    loading: object,
    messages: object,
    openConfirmDialog: func,
    openErrorDialog: func,
    openSuccessDialog: func,
    setHeaderTitle: func,
    setMetadata: func,
  };

  static defaultProps = {
    isCollapsed: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      userDetails: null,
      msgThread: null,        // The message clicked - this is set to the redux object returned in getMessage().
      showNewMsgForm: false,  // For hide/show
      whichForm: undefined,   // Tell child components when New MessageForm is visible
      windowWidth: null,
      expanded: null,
    };

    this.isUnmounted = false;
  }

  // When the component mounts, set these variables
  componentWillMount() {
    this.props.setMetadata({
      activeMenuItemKey: 'messages',
      pageTitle: 'Messages',
      pageDescription: 'View and compose messages',
    });

    this.props.setHeaderTitle('Messages');

    this.setState({
      expanded: !this.props.isCollapsed,
      windowWidth: window.innerWidth,
    });
  }

  // Listen to window and resize event
  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.props.getAllMessageThreadHeaders();
  }

  // On unmount, remove listeners to prevent memory leaks
  componentWillUnmount() {
    this.isUnmounted = true;
    window.removeEventListener('resize', this.handleResize);
  }

  // @TODO replace ContentPanel with SlingPanel
  // @TODO how could this be tied in to SlidingPanel instead?
  handleResize = () => {
    const windowWidth = this.state.windowWidth;
    const mobileContentPanelOpen = this.state.expanded;
    const isDesktop = (windowWidth >= 768 && mobileContentPanelOpen);

    if (!this.isUnmounted) {
      let updatedState = { windowWidth: window.innerWidth };
      if (isDesktop) {
        updatedState.expanded = !mobileContentPanelOpen;
      }

      this.setState(updatedState);
    }
  };

  /*
    This method was repurposed from 'handleChange' to handle manually refreshing
    the inbox when clicking the tab.
    POR-203 - combine inbox/outbox view - keep in mind if Product wants to revisit
    Inbox/Outbox tabs
    @param: tab
  */
  handleRefreshInbox = () => this.props.getAllMessageThreadHeaders();

  handleStateAfterDelete = () => {
    if (this.state.windowWidth <= 768) {
      this.setState({
        expanded: !this.state.expanded,
      });
    }

    this.setState({
      msgThread: null,
    });

    this.handleRefreshInbox();
  };

  // Toggles show/hide when + (Compose) button is clicked
  handleNewMsgFormDisplay = () => {
    if (this.state.windowWidth <= 768) {
      this.setState({
        expanded: !this.state.expanded,
      });
    }

    this.setState({
      showNewMsgForm: !this.state.showNewMsgForm,
      msgThread: null,
    });

    this.props.getPossibleRecipients();
  };

  // Display Messages from thread in ContentPanel onClick
  handleMsgThreadDisplay = (threadId) => {

    const successCallback = () => {
      if (this.state.windowWidth <= 768) {
        this.setState({
          expanded: !this.state.expanded,
        });
      }
      this.setState({
        msgThread: this.props.messages.messageThread,
        showNewMsgForm: false,
      });
    };

    const errorCallback = response =>
      this.props.openErrorDialog({
        content: response.message.body,
        okCallback: this.props.closeAlertDialog,
      });

    this.props.getMessage(threadId, successCallback, errorCallback);

  };

  // Nullify the msgThread on toggle of expanding the mobile ContentPanel
  handleMobileContentPanelDisplay = () => {
    this.setState({
      expanded: !this.state.expanded,
      msgThread: null,
      showNewMsgForm: false,
    });
  };

  // Invoked in MessageForm when Reply or Forward is successful. Updates the UI
  // msgThread with the new message sent and refreshes the Inbox.
  handleUpdateMessageThread = () => {
    const currentThreadId = this.state.msgThread.id;

    this.props.getAllMessageThreadHeaders();

    this.handleMsgThreadDisplay(currentThreadId);
  };

  render = () => {
    const msgThread = this.state.msgThread;
    const newMsgFormIsVisible = this.state.showNewMsgForm;
    const newMsgFormIsVisibleStyle = (this.state.showNewMsgForm ? s.newMsgFormIsVisible : '');

    const mobileContentPanelIsOpen = this.state.expanded;
    const mobileContentPanelStyle = (mobileContentPanelIsOpen ? s.isActive : '');

    return (
      <Layout>
        <ActionsPanel className={cx(s.actionsPanel, s.hideForPrint)}>
          <section className={s.utilitiesActionPanel}>
            <RefreshMsgsButton
              handleRefreshInbox={this.handleRefreshInbox}
            />
            <NewMsgButton
              messageComposeEnabled={this.props.authenticatedPreferences.messageComposeEnabled}
              className={newMsgFormIsVisibleStyle}
              handleNewMsgFormDisplay={this.handleNewMsgFormDisplay}
            />
          </section>
          <MessageThreadHeaderContainer
            messageThreadHeaders={this.props.messages.messageThreadHeaders}
            isLoading={contains(FETCH_MESSAGE_THREAD_HEADERS, this.props.loading.requests)}
            handleMsgThreadDisplay={this.handleMsgThreadDisplay}
          />
        </ActionsPanel>
        <ContentPanel className={cx(s.contentPanel, mobileContentPanelStyle)}>
          {newMsgFormIsVisible && !msgThread ? (
            <MessageForm
              authenticatedPreferences={this.props.authenticatedPreferences}
              closeAlertDialog={this.props.closeAlertDialog}
              forwardMessage={this.props.forwardMessage}
              handleNewMsgFormDisplay={this.handleNewMsgFormDisplay}
              handleRefreshInbox={this.handleRefreshInbox}
              isLoadingForward={contains(FORWARD_MESSAGE, this.props.loading.requests)}
              isLoadingReply={contains(REPLY_MESSAGE, this.props.loading.requests)}
              isLoadingSendNew={contains(SEND_MESSAGE, this.props.loading.requests)}
              isNewMsg
              openSuccessDialog={this.props.openSuccessDialog}
              openErrorDialog={this.props.openErrorDialog}
              recipients={this.props.messages.recipients}
              replyMessage={this.props.replyMessage}
              sendMessage={this.props.sendMessage}
            />
          ) : (
            <div>
              <section className={s.utilitiesContentPanel}>
                {mobileContentPanelIsOpen &&
                  <button
                    type="button"
                    className={s.mobileBackBtn}
                    onClick={this.handleMobileContentPanelDisplay}
                  >
                    &lt;
                  </button>
                }
                {this.state.windowWidth >= 768 &&
                  <MessageThreadUtilities
                    deleteMessage={this.props.deleteMessage}
                    msgThread={msgThread}
                    handleStateAfterDelete={this.handleStateAfterDelete}
                    openSuccessDialog={this.props.openSuccessDialog}
                    closeAlertDialog={this.props.closeAlertDialog}
                    closeConfirmDialog={this.props.closeConfirmDialog}
                    openConfirmDialog={this.props.openConfirmDialog}
                    openErrorDialog={this.props.openErrorDialog}
                  />
                }
                {mobileContentPanelIsOpen &&
                  <MessageThreadUtilities
                    deleteMessage={this.props.deleteMessage}
                    msgThread={msgThread}
                    handleStateAfterDelete={this.handleStateAfterDelete}
                    className={cx(s.hideForDesktop, s.utilitiesMobile)}
                    openSuccessDialog={this.props.openSuccessDialog}
                    closeAlertDialog={this.props.closeAlertDialog}
                    closeConfirmDialog={this.props.closeConfirmDialog}
                    openConfirmDialog={this.props.openConfirmDialog}
                    openErrorDialog={this.props.openErrorDialog}
                  />
                }
              </section>
              <MessageThreadContainer
                authenticatedPreferences={this.props.authenticatedPreferences}
                closeAlertDialog={this.props.closeAlertDialog}
                forwardMessage={this.props.forwardMessage}
                handleUpdateMessageThread={this.handleUpdateMessageThread}
                isLoadingForward={contains(FORWARD_MESSAGE, this.props.loading.requests)}
                isLoadingReply={contains(REPLY_MESSAGE, this.props.loading.requests)}
                isLoadingSendNew={contains(SEND_MESSAGE, this.props.loading.requests)}
                msgThread={msgThread}
                openSuccessDialog={this.props.openSuccessDialog}
                openErrorDialog={this.props.openErrorDialog}
                replyMessage={this.props.replyMessage}
                sendMessage={this.props.sendMessage}
                whichForm={this.state.whichForm}
              />
            </div>
          )}
        </ContentPanel>
      </Layout>
    );
  }
}

const mapStateToProps = (state) => ({
  authenticatedPreferences: state.authenticatedPreferences,
  loading: state.loadingState,
  messages: state.messages,
});

const mapDispatchToProps = dispatch => ({
  closeAlertDialog: () => dispatch(closeAlertDialog()),
  closeConfirmDialog: () => dispatch(closeConfirmDialog()),
  deleteMessage: (messageId, successCb, errorCb) => dispatch(deleteMessage(messageId, successCb, errorCb)),
  getAllMessageThreadHeaders: () => dispatch(getAllMessageThreadHeaders()),
  forwardMessage: (message, successCb, errorCb) => dispatch(forwardMessage(message, successCb, errorCb)),
  getMessage: (threadId, successCb, errorCb) => dispatch(getMessage(threadId, successCb, errorCb)),
  getPossibleRecipients: () => dispatch(getPossibleRecipients()),
  openConfirmDialog: dialog => dispatch(openConfirmDialog(dialog)),
  openErrorDialog: (dialog) => dispatch(openErrorDialog(dialog)),
  openSuccessDialog: (dialog) => dispatch(openSuccessDialog(dialog)),
  replyMessage: (message, successCb, errorCb) => dispatch(replyMessage(message, successCb, errorCb)),
  sendMessage: (message, successCb, errorCb) => dispatch(sendMessage(message, successCb, errorCb)),
  setHeaderTitle: title => dispatch(setHeaderTitle(title)),
  setMetadata: metadata => dispatch(setMetadata(metadata)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LandingPage);



// WEBPACK FOOTER //
// ./src/components/Pages/Messages/LandingPage/LandingPage.js